// lib/marketplace/favoriteSets.ts
//
// Lógica pura del merge de favoritos entre el localStorage del navegador y la
// cuenta (spec §4.3), extraída de useFavorites para poder probarla sin
// @testing-library/react (el repo no lo tiene — ver useFavorites.test.ts).
export function mergeFavoriteSets(
  local: string[],
  account: string[] | undefined,
): { union: string[]; missingInAccount: string[] } {
  // Sin sesión (account === undefined): el navegador manda, tal cual — nada
  // que fusionar ni que subir.
  if (account === undefined) return { union: local, missingInAccount: [] }
  const accountSet = new Set(account)
  const missingInAccount = local.filter((id) => !accountSet.has(id))
  // La cuenta primero: si el navegador y la cuenta ya comparten un id, su
  // posición en la unión la fija la cuenta (da igual para un Set de favoritos,
  // pero mantiene el orden determinista para los tests).
  const union = [...new Set([...account, ...local])]
  return { union, missingInAccount }
}

/**
 * Revisión Task 9 (I2): clave de la marca durable de "ya intenté subir los
 * favoritos de este navegador a esta cuenta" — una por cuenta, para que
 * cambiar de cuenta en el mismo navegador (o cerrar sesión y entrar con otro
 * número) no herede la marca de otra cuenta.
 */
export function favoritesMergedKey(accountId: string): string {
  return `redy:favorites:merged:${accountId}`
}

/**
 * Revisión Task 9 (I2): decide si vale la pena llamar a mergeFavorites en
 * este montaje. Antes, el único guard era un useRef ("mergedOnce") que
 * renace en cada visita a /order — un lote fallido (`ok:false`) se
 * reintentaba en CADA carga, para siempre. Ahora la marca durable
 * (localStorage, por cuenta) es la que sabe si ya se intentó con éxito; y no
 * reintenta solo porque no hay nada nuevo que subir.
 */
export function shouldAttemptMerge(args: {
  missingInAccount: string[]
  alreadyMerged: boolean
}): boolean {
  return args.missingInAccount.length > 0 && !args.alreadyMerged
}

/**
 * Revisión Task 9 (I2): tras un merge `ok`, el servidor puede reportar ids
 * `dropped` (locations que ya no existen) — se sacan de la lista local para
 * que no se vuelvan a intentar subir en la próxima visita.
 */
export function withoutDropped(ids: string[], dropped: string[]): string[] {
  if (dropped.length === 0) return ids
  const droppedSet = new Set(dropped)
  return ids.filter((id) => !droppedSet.has(id))
}
